<template>
  <v-layout
    row
    wrap
  >
    <v-flex
      xs12
      class="text-right"
    >
      <import-dialog
        model="activities"
        :project="projectId"
      />
    </v-flex>

    <v-flex
      v-if="!activities.length"
      xs12
      md6
      offset-md3
    >
      <v-card
        flat
        color="transparent"
      >
        <v-card-title class="title justify-center">
          <div>Getting started with Activities</div>
        </v-card-title>

        <v-card-text class="text-justify">
          <p>
            Activities represents the work to be done on the project.
            You can keep a simple flat list of activities or organize them
            hierarchically to create the Work Breakdown Structure.
          </p>

          <v-alert
            v-if="false"
            type="info"
            outlined
          >
            You have tasks lists in other tools? You can import the tasks into Atlas
            or sync external tools with Atlas Integrations.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-flex>

    <v-flex xs12>
      <v-card style="position:relative;overflow:hidden">
        <pa-gantt
          :tasks="activities.models"
          task-name="$.name"
          task-start="$.startDate"
          task-end="$.dueDate"
          task-parent="$.parentId"
          task-progress="$.progress"
          :selected.sync="selectedActivities"
          @add-task="newActivity"
          @edit-task="editActivity"
          @link-tasks="linkActivities"
        >
          <template v-slot:drawer="{ close, selectedActivity }">
            <v-card
              v-if="selectedActivity"
              flat
              tile
            >
              <v-card-title class="align-start">
                <v-tooltip v-if="!selectedActivity.isEditing" left>
                  <template #activator="{ on }">
                    <span
                      class="text-break"
                      style="flex:1;cursor:pointer"
                      @click="selectedActivity.isEditing = true"
                      v-on="on"
                    >
                      {{ selectedActivity.$.name }}
                    </span>
                  </template>

                  <span v-t="'clickToEdit'" />
                </v-tooltip>

                <v-text-field
                  v-else
                  v-model="selectedActivity.name"
                  outlined
                  append-icon="save"
                  autofocus
                  single-line
                  :hint="$t('enterToSave')"
                  @blur="selectedActivity.reset(); selectedActivity.isEditing = false"
                  @click:append="selectedActivity.save(); selectedActivity.isEditing = false"
                  @keypress.enter="selectedActivity.save(); selectedActivity.isEditing = false"
                />

                <v-btn
                  icon
                  @click="close()"
                >
                  <v-icon>close</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text>
                <div class="d-flex align-center justify-space-around flex-wrap">
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-btn
                        icon
                        :color="selectedActivity.$.progress === 100 ? 'primary' : null"
                        :disabled="selectedActivity.saving"
                        @click="toggleCompleted(selectedActivity)"
                        v-on="on"
                      >
                        <v-icon>
                          {{ selectedActivity.$.progress === 100 ? 'check_circle' : 'check_circle_outline' }}
                        </v-icon>
                      </v-btn>
                    </template>

                    <span
                      v-t="selectedActivity.$.progress === 100 ? 'activities.completed' : 'activities.markCompleted'"
                    />
                  </v-tooltip>

                  <v-menu
                    v-model="showUserPickerMenu"
                    :close-on-content-click="false"
                  >
                    <template v-slot:activator="{ on }">
                      <pa-user-avatar
                        :user-id="selectedActivity.$.owner"
                        type="chip"
                        no-user-text="Unassigned"
                        :disabled="!!selectedActivity.saving"
                        v-on="on"
                        @click="selectedActivity.owner = null"
                        @click:close="selectedActivity.owner = null; selectedActivity.save()"
                      />
                    </template>

                    <pa-user-picker
                      v-model="selectedActivity.owner"
                      :label="$t('activities.owner')"
                      solo
                      @input="selectedActivity.save(); showUserPickerMenu = false"
                    />
                  </v-menu>

                  <activity-dates-picker :value="selectedActivity" />
                </div>

                <v-list
                  two-line
                  subheader
                >
                  <v-list-item
                    v-for="customProp in customProperties('activity')"
                    :key="`custom-prop-${customProp.id}`"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ selectedActivity[customProp.key] || '-' }}</v-list-item-title>
                      <v-list-item-subtitle>{{ customProp.name }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <v-row>
                  <v-col class="text-right">
                    <v-btn
                      v-t="'delete'"
                      text
                      color="error"
                      @click="deleteActivity(selectedActivity)"
                    />
                  </v-col>
                </v-row>

                <v-divider class="mb-4" />

                <comments
                  :doc-ref="selectedActivity.id"
                  :project="projectId"
                />
              </v-card-text>
            </v-card>
          </template>
        </pa-gantt>
      </v-card>
    </v-flex>

    <pa-dialog
      :title="(activityEdit.isExisting() ? $t('edit') : $t('create')) + ' ' + $tc('activities._', 1)"
      :subtitle="activityEdit.isExisting() ? activityEdit.$.name : null"
      :positive-text="activityEdit.isExisting() ? $t('save') : $t('create')"
      :negative-text="activityEdit.isExisting() ? $t('delete') : null"
      :show="!!showDialog"
      :loading="activityEdit.saving"
      lazy
      @positive="saveActivity"
      @cancel="cancelEditActivity"
      @negative="deleteActivity(activityEdit)"
    >
      <v-flex xs12>
        <v-text-field
          v-model.trim="activityEdit.name"
          :label="$t('name') + ' *'"
          :error-messages="activityEdit.errors.name"
        />
      </v-flex>

      <v-flex
        xs12
        sm6
      >
        <pa-date-picker
          v-model="activityEdit.startDate"
          :label="$t('activities.start')"
          close-on-click
        />
      </v-flex>

      <v-flex
        xs12
        sm6
      >
        <pa-date-picker
          v-model="activityEdit.dueDate"
          :label="$t('activities.end')"
          :allowed-dates="allowedDueDates"
          close-on-click
        />
      </v-flex>

      <v-flex
        xs12
        sm6
      >
        <pa-user-picker
          v-model="activityEdit.owner"
          :label="$t('activities.owner')"
        />
      </v-flex>

      <v-flex
        xs12
        sm6
      >
        <v-text-field
          v-model.number="activityEdit.effort"
          :label="$t('activities.effort')"
          suffix="hrs"
          :error-messages="activityEdit.errors.effort"
          type="number"
          min="0"
        />
      </v-flex>

      <v-flex xs12>
        <v-slider
          v-model.number="activityEdit.progress"
          :label="$t('activities.progress')"
          thumb-label="always"
        />
      </v-flex>

      <!-- Custom Properties -->
      <v-flex
        v-for="customProp in customProperties('activity')"
        :key="`custom-prop-${customProp.id}`"
        xs12
        sm6
      >
        <pa-custom-property-field
          v-model="activityEdit[customProp.key]"
          :custom-prop="customProp"
        />
      </v-flex>

      <v-flex
        v-if="$can('organization')"
        xs12
        class="text-center"
      >
        <v-btn
          outlined
          small
          :to="{ name: 'organization-fields' }"
        >
          <v-icon left>
            add
          </v-icon>
          {{ $t('add') + ' ' + $t('customFields._') }}
        </v-btn>
      </v-flex>
    </pa-dialog>
  </v-layout>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapState } from 'vuex'

import ProjectComponent from '@/mixins/ProjectComponent'
import PaDatePicker from '@/components/shared/PaDatePicker'
import PaDialog from '@/components/shared/PaDialog'
import Activities from '@/models/Activities'
import Activity from '@/models/Activity'
import ActivityDatesPicker from './ActivityDatesPicker'
import Comments from '@/components/activities/Comments'
import PaGantt from '@/components/shared/PaGantt'
import ImportDialog from '@/components/import/ImportDialog'
import PaCustomPropertyField from '@/components/shared/PaCustomPropertyField'

export default {
  name: 'ProjectActivities',
  components: {
    ActivityDatesPicker,
    Comments,
    ImportDialog,
    PaDatePicker,
    PaDialog,
    PaGantt,
    PaCustomPropertyField
  },
  mixins: [ ProjectComponent ],
  data () {
    return {
      activities: new Activities(),
      activityDates: [],
      activityEdit: new Activity({ project: this.projectId }),
      // selectedActivity: null,
      // selectedActivityRef: null,
      selectedActivities: [],
      showDialog: false,
      showUserPickerMenu: false
    }
  },
  computed: {
    ...mapGetters('organizations', [ 'customProperties' ]),
    ...mapState('user', [ 'integrations' ]),
    allowedDueDates () {
      if (!this.activityEdit.startDate) {
        return null
      } else {
        return (dueDate) => {
          return dueDate > this.activityEdit.startDate
        }
      }
    },
    activitiesTree () {
      let activitiesTree = []
      let map = {}

      this.activities.each((activity, i) => {
        activity.children = []
        activity.childrenCount = [0, 0]
        map[activity.id] = i
      })

      this.activities.each((activity, i) => {
        if (activity.parentId) {
          const parent = this.activities.models[map[activity.parentId]]
          parent.childrenCount[0] += activity.childrenCount[0]
          parent.childrenCount[1] += (activity.childrenCount[1] + 1)
          if (activity.completed) { parent.childrenCount[0]++ }

          activity.parent = parent
          parent.children.push(activity)
        } else {
          activitiesTree.push(activity)
        }
      })

      return activitiesTree
    },
    totalEffort () {
      let total = 0
      this.activities.each(activity => {
        if (activity.effort) {
          total += activity.effort
        }
      })
      return total
    }
  },
  created () {
    if (this.$route.query.a) {
      this.selectedActivities = [ this.$route.query.a ]
    }
  },
  methods: {
    async loadData () {
      this.activities = new Activities()

      await this.activities.fetch({
        project: this.projectId
      })
    },
    async saveActivity () {
      const errors = await this.activityEdit.validate()
      if (Object.keys(errors).length) { return }

      const changedAttributes = this.activityEdit.changed() || []

      await this.activityEdit.save()

      // if any of the trigger attributes is changed, then
      // recalc all parents
      const TRIGGER_ATTRS = ['startDate', 'dueDate', 'progress']

      if (changedAttributes.some(attr => TRIGGER_ATTRS.includes(attr))) {
        // update all parents
        if (this.activityEdit.parent) {
          await this.calculateParent(this.activityEdit.parent)
        }
      }

      if (changedAttributes.includes('progress')) {
        // calculate project progress as avg of top-level activities
        const projectProgress = Math.round(
          this.activitiesTree.reduce((tot, a) => (a.progress || 0) + tot, 0) /
          this.activitiesTree.length
        )

        this.project.progress = projectProgress
        await this.project.save()
      }

      this.resetNewActivityForm()
      this.showDialog = false
    },
    async linkActivities () {
      for (let i = 0; i < this.selectedActivities.length - 1; i++) {
        let activity = this.activities.find({ id: this.selectedActivities[i] })
        activity.successors[this.selectedActivities[i + 1]] = 'FS'
        await activity.save()
      }
    },
    async toggleCompleted (activity) {
      if (activity.progress === 100) {
        activity.progress = 0
      } else {
        activity.progress = 100
      }

      await activity.save()
    },
    newActivity ({ parentId = null, order = 0 } = {}) {
      this.resetNewActivityForm(parentId, order)
      this.showDialog = true
    },
    editActivity (activity) {
      this.activityEdit = activity
      this.activityEdit.reset()
      this.showDialog = true
    },
    async deleteActivity (item) {
      let allActs = { }
      allActs[item.id] = null

      const traverse = async (items) => {
        for (let child of items) {
          await child.delete()
          await traverse(child.children)
        }
      }

      await traverse(item.children)
      await item.delete()
      this.showDialog = false
    },
    // onClickActivity (activity) {
    //   this.selectedActivity = activity
    //   this.selectedActivityRef = activity.id
    // },
    async calculateParent (activity) {
      // check if we reached the rootTask (id === null)
      if (activity.children.length && activity.id) {
        activity.startDate = moment.min(activity.children.map(a => moment(a.startDate))).toDate()
        activity.dueDate = moment.max(activity.children.map(a => moment(a.dueDate))).toDate()
        activity.progress = activity.children.reduce((tot, a) => (a.progress || 0) + tot, 0) /
          activity.children.length
        await activity.save()
      }

      if (activity.parent) {
        await this.calculateParent(activity.parent)
      }
    },
    resetNewActivityForm (parentId = null, order = 0) {
      this.activityEdit = new Activity({
        project: this.projectId,
        parentId,
        order
      },
      this.activities)
    },
    cancelEditActivity () {
      this.activityEdit.reset()
      this.showDialog = false
    }
  }
}
</script>

<style lang="scss">
.activities-tree {
  .completed {
    text-decoration: line-through;
    color: lightgray;
  }
}

.pa-gantt__tasks-header > div:last-child {
  flex-grow: 0;
  min-width: 88px;
}
</style>
